import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { criticalityMatrixApi, oesApi, settingsApi } from '../services/api';
import {
  getCriticalityLabels,
  getLevelLabels,
  getLevelColors,
  getMatrixCellBg,
  getMatrixCellText,
  getScoreBarColors,
} from '../utils/settingsScales';
import Layout from '../components/layout/Layout';
import { useLabels } from '../providers/LabelsProvider';
import { useT } from '../providers/I18nProvider';

const LEVEL_BORDER_COLORS = {
  'ma': 'border-red-200',
  'a': 'border-orange-200',
  'm': 'border-amber-200',
  'b': 'border-blue-200',
  'mb': 'border-emerald-200',
};

const LEVEL_DOT_COLORS = { ma: '#ef4444', a: '#f97316', m: '#eab308', b: '#3b82f6', mb: '#22c55e' };
const LEVEL_BADGE_BG = {
  ma: 'bg-red-50 text-red-700 ring-red-100',
  a: 'bg-orange-50 text-orange-700 ring-orange-100',
  m: 'bg-amber-50 text-amber-700 ring-amber-100',
  b: 'bg-blue-50 text-blue-700 ring-blue-100',
  mb: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
};

const SOFT_CELL_BG = { ma: '#fecaca', a: '#fed7aa', m: '#fde68a', b: '#bfdbfe', mb: '#bbf7d0' };
const SOFT_CELL_RING = { ma: '#f87171', a: '#fb923c', m: '#facc15', b: '#60a5fa', mb: '#4ade80' };

// Posture-style bar: label and value above a slim rounded-full bar; fills progressively on mount.
function ImpactFactorRow({ label, score, scoreBarColors }) {
  const value = Math.min(5, Math.max(0, Number(score) || 0));
  const rounded = Math.min(5, Math.max(1, Math.round(value)));
  const pct = (value / 5) * 100;
  const barColor = (scoreBarColors && scoreBarColors[rounded]) || 'bg-gray-400';
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const id = setTimeout(() => setWidth(pct), 50);
    return () => clearTimeout(id);
  }, [pct]);
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="font-medium text-slate-700 truncate pr-2" title={label}>{label}</span>
        <span className="text-slate-600 flex-shrink-0">{value.toFixed(1)} / 5</span>
      </div>
      <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-700 ease-out ${barColor}`}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}

export default function OESAssessmentResult() {
  const labels = useLabels();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const { oesId, assessmentId } = useParams();
  const [matrix, setMatrix] = useState(null);
  const [assessment, setAssessment] = useState(location.state?.assessment ?? null);
  const [oes, setOes] = useState(null);
  const [scales, setScales] = useState(null);
  const [loading, setLoading] = useState(!!assessmentId && !location.state?.assessment);
  const fromAdminList = !!assessmentId;

  const isAdmin = ['global_admin', 'admin', 'assessment_admin'].includes(localStorage.getItem('role') || '');

  // Regular users: use their context (GET /user/settings returns their OES's org settings).
  // Admins: we load settings for the OES's organization below, once oes is available.
  useEffect(() => {
    if (isAdmin) return; // admins get settings when oes is loaded (by organization_id)
    settingsApi.getForCurrentUser()
      .then((r) => setScales(r.data?.scales ?? {}))
      .catch(() => setScales({}));
  }, [isAdmin]);

  useEffect(() => {
    if (!assessment?.oes_id) return;
    const fetchOes = async () => {
      try {
        const response = await oesApi.getById(assessment.oes_id);
        setOes(response.data);
      } catch (err) {
        console.error('Error fetching OES:', err);
      }
    };
    fetchOes();
  }, [assessment?.oes_id]);

  // Admins viewing a scorecard: load settings for the OES's organization so labels match that org's config.
  useEffect(() => {
    if (!isAdmin || !oes?.organization_id) return;
    settingsApi.get('organization', oes.organization_id)
      .then((r) => setScales(r.data?.scales ?? {}))
      .catch(() => setScales({}));
  }, [isAdmin, oes?.organization_id]);

  useEffect(() => {
    const fetchMatrix = async () => {
      try {
        const response = await criticalityMatrixApi.getMatrix();
        setMatrix(response.data);
      } catch (err) {
        console.error('Error fetching matrix:', err);
      }
    };
    fetchMatrix();
  }, []);

  useEffect(() => {
    if (!assessmentId || location.state?.assessment) return;
    const fetchAssessment = async () => {
      try {
        const response = await oesApi.getAssessmentById(assessmentId);
        setAssessment(response.data);
      } catch (err) {
        console.error('Error fetching assessment:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAssessment();
  }, [assessmentId, location.state?.assessment]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">{t('assessment.loadingAssessment')}</p>
        </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('assessment.noAssessmentData')}</h2>
          <button
            onClick={() => navigate(fromAdminList ? '/admin/assessment-results' : '/oes')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {fromAdminList ? t('assessment.backToCompleted') : t('assessment.returnToManagement', { entity: labels.entityName })}
          </button>
        </div>
      </div>
    );
  }

  const criticalityLabels = getCriticalityLabels(scales);
  const levelLabels = getLevelLabels(scales);
  const levelColors = getLevelColors(scales);
  const matrixCellBg = getMatrixCellBg(scales);
  const matrixCellText = getMatrixCellText(scales);
  const scoreBarColors = getScoreBarColors(scales);

  const impactAvg = assessment.impact_average.toFixed(2);
  const impactLevel = assessment.impact_level.toUpperCase();
  const automationLevel = assessment.automation_level.toUpperCase();
  const finalCriticality = assessment.oes_criticality.toUpperCase();

  // Impact by service: average of 5 factors per service (same formula as backend for impact)
  const serviceImpactRanking = (assessment.services || [])
    .map((s) => {
      const alcance = Number(s.user_count_score) || 1;
      const frecuencia = Number(s.frequency_score) || 1;
      const reemplazabilidad = 6 - (Number(s.replaceability_score) || 1);
      const interdependencia = Number(s.interdependency_score) || 1;
      const continuidad = 6 - (Number(s.time_to_impact_score) || 1);
      const avg = (alcance + frecuencia + reemplazabilidad + interdependencia + continuidad) / 5;
      return { name: s.service_name || 'Service', avg };
    })
    .sort((a, b) => b.avg - a.avg);

  // Automation by process: rank by automation_level (1-5)
  const processAutomationRanking = [...(assessment.processes || [])]
    .sort((a, b) => (Number(b.automation_level) || 0) - (Number(a.automation_level) || 0))
    .map((p) => ({ name: p.process_name || 'Process', level: Number(p.automation_level) || 1 }));

  const scoreToLevelKey = (score) => {
    if (score >= 4.5) return 'ma';
    if (score >= 3.5) return 'a';
    if (score >= 2.5) return 'm';
    if (score >= 1.5) return 'b';
    return 'mb';
  };

  const MatrixCell = ({ rowLevel, colLevel, isResult }) => {
    const cellValue = matrix?.matrix[rowLevel][colLevel];
    const isHighlighted = isResult &&
      rowLevel === impactLevel &&
      colLevel === automationLevel;
    const critKey = cellValue?.toLowerCase() || '';
    const bg = SOFT_CELL_BG[critKey] || '#f8fafc';

    return (
      <div
        className={`relative px-2.5 py-4 text-center font-semibold transition-all text-xs ${isHighlighted ? 'z-10' : ''}`}
        style={{
          backgroundColor: bg,
          ...(isHighlighted ? { boxShadow: `0 0 0 2.5px #3b82f6, 0 0 0 5px rgba(59,130,246,0.15)`, borderRadius: 6, transform: 'scale(1.04)' } : {}),
        }}
      >
        <span style={{ color: isHighlighted ? '#1e40af' : '#64748b' }}>
          {cellValue ? criticalityLabels[critKey] : ''}
        </span>
        {isHighlighted && (
          <div className="absolute -top-1.5 -right-1.5 bg-blue-500 text-white rounded-full w-4 h-4 flex items-center justify-center">
            <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
          </div>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Result Header */}
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden mb-6">
          <div className="px-8 py-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                {oes?.logo && (
                  <img
                    src={oes.logo}
                    alt=""
                    className="w-12 h-12 rounded-xl object-contain bg-slate-50 border border-slate-200"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">{t('assessment.criticalityAssessment', { entity: labels.entityName })}</p>
                  <h1 className="text-xl font-bold text-slate-800">{oes?.name || 'Assessment Results'}</h1>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">{t('assessment.finalCriticality')}</p>
                <span className={`inline-flex px-3.5 py-1.5 rounded-full text-sm font-bold ring-1 ${LEVEL_BADGE_BG[finalCriticality.toLowerCase()] || 'bg-slate-50 text-slate-600 ring-slate-200'}`}>
                  {criticalityLabels[finalCriticality.toLowerCase()]}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Assessment Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Impact Factors */}
          <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">{t('assessment.impactAnalysis')}</h2>
            <div className="space-y-4">
              <p className="text-xs font-medium text-gray-500 mb-1">{t('assessment.servicesImpact')}</p>
              <ImpactFactorRow label={t('assessment.reachScope')} score={assessment.alcance_score} scoreBarColors={scoreBarColors} />
              <ImpactFactorRow label={t('assessment.frequency')} score={assessment.frecuencia_score} scoreBarColors={scoreBarColors} />
              <ImpactFactorRow label={t('assessment.irreplaceability')} score={assessment.reemplazabilidad_score} scoreBarColors={scoreBarColors} />
              <ImpactFactorRow label={t('assessment.interdependency')} score={assessment.interdependencia_score} scoreBarColors={scoreBarColors} />
              <ImpactFactorRow label={t('assessment.disruptionSpeed')} score={assessment.continuidad_score} scoreBarColors={scoreBarColors} />
              {assessment.cyberattack_impact && (
                <>
                  <ImpactFactorRow label={t('assessment.impactNationalSecurity')} score={assessment.cyberattack_impact.security_impact} scoreBarColors={scoreBarColors} />
                  <ImpactFactorRow label={t('assessment.impactEconomy')} score={assessment.cyberattack_impact.economy_impact} scoreBarColors={scoreBarColors} />
                  <ImpactFactorRow label={t('assessment.impactHealth')} score={assessment.cyberattack_impact.health_impact} scoreBarColors={scoreBarColors} />
                  <ImpactFactorRow label={t('assessment.impactPrivacy')} score={assessment.cyberattack_impact.privacy_impact} scoreBarColors={scoreBarColors} />
                </>
              )}
              <div className={`flex justify-between items-center p-4 rounded-xl border ${LEVEL_BORDER_COLORS[impactLevel.toLowerCase()]} bg-slate-50/50`}>
                <span className="font-semibold text-slate-700 text-sm">{t('assessment.impactAverage')}</span>
                <div className="flex items-center gap-3">
                  <span className="font-bold text-lg text-slate-800 tabular-nums">{impactAvg}</span>
                  <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold ring-1 ${LEVEL_BADGE_BG[impactLevel.toLowerCase()] || 'bg-slate-50 text-slate-600 ring-slate-200'}`}>
                    {levelLabels[impactLevel.toLowerCase()]}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Automation Factor */}
          <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">{t('assessment.itOtAutomation')}</h2>
            <div className="space-y-4">
              <ImpactFactorRow label={t('assessment.automationScore')} score={assessment.automation_score} scoreBarColors={scoreBarColors} />
              <div className={`flex justify-between items-center p-4 rounded-xl border ${LEVEL_BORDER_COLORS[automationLevel.toLowerCase()]} bg-slate-50/50`}>
                <span className="font-semibold text-slate-700 text-sm">{t('assessment.automationLevel')}</span>
                <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold ring-1 ${LEVEL_BADGE_BG[automationLevel.toLowerCase()] || 'bg-slate-50 text-slate-600 ring-slate-200'}`}>
                  {levelLabels[automationLevel.toLowerCase()]}
                </span>
              </div>

              {/* Calculation Visual */}
              <div className="mt-6 p-5 bg-slate-50 rounded-xl border border-slate-200">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider text-center mb-3">{t('assessment.criticalityCalculation')}</p>
                <div className="flex items-center justify-center gap-3">
                  <span className={`px-3 py-1.5 rounded-full text-xs font-bold ring-1 ${LEVEL_BADGE_BG[impactLevel.toLowerCase()] || ''}`}>
                    {levelLabels[impactLevel.toLowerCase()]}
                  </span>
                  <span className="text-lg text-slate-300 font-light">×</span>
                  <span className={`px-3 py-1.5 rounded-full text-xs font-bold ring-1 ${LEVEL_BADGE_BG[automationLevel.toLowerCase()] || ''}`}>
                    {levelLabels[automationLevel.toLowerCase()]}
                  </span>
                  <span className="text-lg text-slate-300 font-light">=</span>
                  <span className={`px-4 py-2 rounded-full text-sm font-bold ring-1 ${LEVEL_BADGE_BG[finalCriticality.toLowerCase()] || ''}`}>
                    {criticalityLabels[finalCriticality.toLowerCase()]}
                  </span>
                </div>
                <p className="text-[10px] text-slate-400 text-center mt-2.5">
                  {t('assessment.criticalityFormula', { entity: labels.entityName })}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Impact by service & Automation by process */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">{t('assessment.impactByService')}</h2>
            <p className="text-[10px] text-slate-400 mb-4">{t('assessment.impactByServiceDesc')}</p>
            {serviceImpactRanking.length === 0 ? (
              <p className="text-slate-400 text-xs text-center py-3">{t('assessment.noServicesAssessed')}</p>
            ) : (
              <ul className="space-y-0.5">
                {serviceImpactRanking.map((item, idx) => {
                  const key = scoreToLevelKey(item.avg);
                  const barColor = LEVEL_DOT_COLORS[key] || '#94a3b8';
                  return (
                    <li key={idx} className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-50 transition-colors">
                      <span className="text-[10px] font-mono text-slate-300 w-4 text-right">{idx + 1}</span>
                      <span className="text-xs font-medium text-slate-600 truncate flex-1" title={item.name}>{item.name}</span>
                      <div className="w-20 h-2 bg-slate-100 rounded-full overflow-hidden flex-shrink-0">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(item.avg / 5) * 100}%`, backgroundColor: barColor }} />
                      </div>
                      <span className="text-[10px] font-bold text-slate-400 tabular-nums w-7 text-right">{item.avg.toFixed(1)}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">{t('assessment.automationByProcess')}</h2>
            <p className="text-[10px] text-slate-400 mb-4">{t('assessment.automationByProcessDesc')}</p>
            {processAutomationRanking.length === 0 ? (
              <p className="text-slate-400 text-xs text-center py-3">{t('assessment.noProcessesAssessed')}</p>
            ) : (
              <ul className="space-y-0.5">
                {processAutomationRanking.map((item, idx) => {
                  const key = ['mb', 'b', 'm', 'a', 'ma'][Math.min(4, Math.max(0, item.level - 1))];
                  const barColor = LEVEL_DOT_COLORS[key] || '#94a3b8';
                  return (
                    <li key={idx} className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-50 transition-colors">
                      <span className="text-[10px] font-mono text-slate-300 w-4 text-right">{idx + 1}</span>
                      <span className="text-xs font-medium text-slate-600 truncate flex-1" title={item.name}>{item.name}</span>
                      <div className="w-20 h-2 bg-slate-100 rounded-full overflow-hidden flex-shrink-0">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(item.level / 5) * 100}%`, backgroundColor: barColor }} />
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ring-1 flex-shrink-0 ${LEVEL_BADGE_BG[key] || 'bg-slate-50 text-slate-500 ring-slate-200'}`}>
                        {levelLabels[key]}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Criticality Matrix Reference */}
        {matrix && (
          <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm mb-6 p-6">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">{t('assessment.criticalityMatrixRef')}</h2>
            <div className="overflow-x-auto">
              <div className="flex items-stretch w-full max-w-5xl mx-auto">
                <div className="flex items-center justify-center py-2 pr-1.5" style={{ minWidth: 22 }}>
                  <span
                    className="text-[9px] font-semibold uppercase tracking-widest text-slate-400 whitespace-nowrap"
                    style={{ writingMode: 'vertical-rl', transform: 'rotate(-180deg)' }}
                  >
                    Impact
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="grid grid-cols-6" style={{ gap: '1px', background: '#e2e8f0' }}>
                    {/* Corner */}
                    <div className="px-2.5 py-4 flex items-center justify-center bg-slate-50">
                      <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400"></span>
                    </div>
                    {['MB', 'B', 'M', 'A', 'MA'].map(level => (
                      <div key={level} className="px-2.5 py-4 flex items-center justify-center bg-slate-50">
                        <span className="text-[10px] font-semibold text-slate-500">{levelLabels[level.toLowerCase()]}</span>
                      </div>
                    ))}
                    {/* Data rows */}
                    {['MA', 'A', 'M', 'B', 'MB'].map(rowLevel => (
                      <React.Fragment key={rowLevel}>
                        <div className="px-2.5 py-4 flex items-center justify-center bg-slate-50">
                          <span className="text-[10px] font-semibold text-slate-500">{levelLabels[rowLevel.toLowerCase()]}</span>
                        </div>
                        {['MB', 'B', 'M', 'A', 'MA'].map(colLevel => (
                          <MatrixCell
                            key={`${rowLevel}-${colLevel}`}
                            rowLevel={rowLevel}
                            colLevel={colLevel}
                            isResult={true}
                          />
                        ))}
                      </React.Fragment>
                    ))}
                  </div>
                  <div className="flex justify-center py-2">
                    <span className="text-[9px] font-semibold uppercase tracking-widest text-slate-400">Automation</span>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-400 text-center mt-3">
              Your result: <span className="font-medium text-slate-500">{levelLabels[impactLevel.toLowerCase()]}</span> × <span className="font-medium text-slate-500">{levelLabels[automationLevel.toLowerCase()]}</span> = <span className="font-semibold text-slate-600">{criticalityLabels[finalCriticality.toLowerCase()]}</span>
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-center gap-4">
          <button
            onClick={() => navigate(fromAdminList ? '/admin/assessment-results' : '/oes')}
            className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold"
          >
            {fromAdminList ? t('assessment.backToCompleted') : t('assessment.returnToManagement', { entity: labels.entityName })}
          </button>
          {!fromAdminList && (
            <button
              onClick={() => navigate(`/oes/${assessment.oes_id}/assessment`)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
            >
              {t('assessment.reassess', { entity: labels.entityName })}
            </button>
          )}
        </div>
      </div>
    </Layout>
  );
}
